import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Center, Group, Loader, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { clearSessionExpiry, logoutSession } from '../api/auth';
import { fetchSubmissionResult } from '../api/result';
import PageShell from '../components/PageShell';
import { useProtectedSession } from '../hooks/useProtectedSession';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import { buildReturnToPath } from '../utils/portalPath';

export default function VoucherResultPage() {
  const { resultId } = useParams<{ resultId: string }>();
  const { data: config, isLoading, sessionScopeKey } = useResolvedProgram();
  const sessionQuery = useProtectedSession(sessionScopeKey);
  const navigate = useNavigate();
  const location = useLocation();
  const logoutMutation = useMutation({ mutationFn: logoutSession });
  const resultQuery = useQuery({
    queryKey: ['voucher-result', resultId],
    queryFn: () => fetchSubmissionResult('voucher-submissions', resultId ?? ''),
    enabled: Boolean(resultId && config && !sessionQuery.isUnauthorized),
    retry: false,
  });

  if (!resultId) return <Navigate to="/" replace />;
  if (isLoading || sessionQuery.isLoading || resultQuery.isLoading) {
    return <Center h="100vh"><Loader size="lg" /></Center>;
  }
  if (sessionQuery.isUnauthorized) {
    return <Navigate to="/auth/request" state={{ returnTo: buildReturnToPath(location) }} replace />;
  }
  if (!config || !config.voucherSubmission || sessionQuery.isError || resultQuery.isError || !resultQuery.data) {
    return <Alert color="red" title="Result Error">Unable to load this voucher result.</Alert>;
  }

  const record = resultQuery.data;
  const isDocuments = record.detailType === 'voucher-documents';
  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      if (sessionScopeKey) clearSessionExpiry(sessionScopeKey);
      navigate('/auth/request', { replace: true });
    }
  };
  return (
    <PageShell
      config={config}
      sessionEmail={sessionQuery.data?.email}
      onGoHome={() => navigate('/')}
      onLogout={() => { void handleLogout(); }}
      isLoggingOut={logoutMutation.isPending}
    >
      <Paper p="xl" radius="md" withBorder className="portal-panel">
        <Stack gap="md">
          <Group gap="sm">
            <ThemeIcon color="green" size="lg" radius="xl" variant="light"><IconCheck size={20} /></ThemeIcon>
            <Title order={3}>{isDocuments ? 'Documents Submitted' : 'Voucher Submission Received'}</Title>
          </Group>
          <Text>
            {isDocuments
              ? 'The voucher documents were submitted successfully.'
              : 'The voucher submission was received successfully.'}
          </Text>
        </Stack>
      </Paper>
      <Button fullWidth onClick={() => navigate('/')} className="portal-primary-button">
        Return Home
      </Button>
    </PageShell>
  );
}